import minBy from "lodash/minBy";
import uniqBy from "lodash/uniqBy";
import { clamp01, distToRect, pickNearestElementAnchor } from "views/components/visualizations/OnClickPopover/anchors";
import dropdownPopover from "views/components/visualizations/OnClickPopover/dropdownPopover";
import CartesianOnClickPopoverDropdown from "views/components/visualizations/OnClickPopover/CartesianOnClickPopoverDropdown";
import { CANDIDATE_PICK_RADIUS } from "views/components/visualizations/Constants";
const getScatterMarkerElement = (graphDiv, pt) => {
  var _a;
  const {
    fullData: { uid },
    pointIndex
  } = pt;
  const trace = graphDiv.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  return (_a = trace.querySelectorAll(".points .point")[pointIndex]) != null ? _a : null;
};
const projectT = (P, A, B) => {
  const vx = B.x - A.x;
  const vy = B.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const len2 = vx * vx + vy * vy || 1;
  return (wx * vx + wy * vy) / len2;
};
const dataToPagePx = (gd, pt, xVal, yVal) => {
  var _a, _b, _c, _d, _e, _f;
  const fl = gd._fullLayout;
  const xa = ((_a = pt.xaxis) == null ? void 0 : _a.l2p) ? pt.xaxis : fl[(_c = (_b = pt.xaxis) == null ? void 0 : _b._name) != null ? _c : "xaxis"];
  const ya = ((_d = pt.yaxis) == null ? void 0 : _d.l2p) ? pt.yaxis : fl[(_f = (_e = pt.yaxis) == null ? void 0 : _e._name) != null ? _f : "yaxis"];
  if (!(xa == null ? void 0 : xa.l2p) || !(xa == null ? void 0 : xa.d2l) || !(ya == null ? void 0 : ya.l2p) || !(ya == null ? void 0 : ya.d2l)) return null;
  const gx = xa.l2p(xa.d2l(xVal));
  const gy = ya.l2p(ya.d2l(yVal));
  if (!Number.isFinite(gx) || !Number.isFinite(gy)) return null;
  const rect = gd.getBoundingClientRect();
  return { x: rect.left + fl._size.l + gx, y: rect.top + fl._size.t + gy };
};
function pickNearestLinesElementForTrace(gd, pt, click) {
  var _a;
  const uid = (_a = pt.fullData) == null ? void 0 : _a.uid;
  if (!uid) return null;
  const trace = gd.querySelector(`.scatterlayer .trace.trace${uid}`);
  if (!trace) return null;
  const linesGroups = Array.from(trace.querySelectorAll(".lines path.js-line"));
  if (!linesGroups.length) return null;
  const candidates = linesGroups.map((line) => {
    const r = line.getBoundingClientRect();
    const { x, y } = click;
    return { el: line, d: distToRect(r, { x, y }) };
  });
  const best = minBy(candidates, "d");
  return best == null ? void 0 : best.el;
}
const getScatterLineElements = (gd, click, pt) => {
  var _a, _b, _c, _d, _e, _f;
  const fd = (_b = pt.data) != null ? _b : (_a = gd._fullData) == null ? void 0 : _a[pt.curveNumber];
  const xs = (_c = fd == null ? void 0 : fd.x) != null ? _c : [];
  const ys = (_d = fd == null ? void 0 : fd.y) != null ? _d : [];
  const i = (_f = (_e = pt.pointIndex) != null ? _e : pt.pointNumber) != null ? _f : 0;
  const segs = [i > 0 ? [i - 1, i] : null, i < xs.length - 1 ? [i, i + 1] : null].filter((seg) => !!seg);
  return segs.map(([i0, i1]) => {
    const A = dataToPagePx(gd, pt, xs[i0], ys[i0]);
    const B = dataToPagePx(gd, pt, xs[i1], ys[i1]);
    if (!A || !B) return null;
    const t = clamp01(projectT(click, A, B));
    const px = A.x + t * (B.x - A.x);
    const py = A.y + t * (B.y - A.y);
    const d = Math.hypot(click.x - px, click.y - py);
    const el = pickNearestLinesElementForTrace(gd, pt, click);
    const { x: valuePx, y: valuePy } = dataToPagePx(gd, pt, xs[i], ys[i]);
    return { el, pt, d, px, py, valuePx, valuePy };
  });
};
const makeScatterAnchor = (e, gd) => {
  const graphDiv = gd;
  const markerCandidates = e.points.map((pt2) => {
    const el2 = getScatterMarkerElement(graphDiv, pt2);
    return el2 ? { pt: pt2, el: el2, rect: el2.getBoundingClientRect() } : null;
  }).filter((candidate) => !!candidate);
  const bestMarker = pickNearestElementAnchor(e, markerCandidates);
  if (bestMarker) return bestMarker;
  const { clientX, clientY } = e.event;
  const click = { x: clientX, y: clientY };
  const lineCandidates = e.points.flatMap((pt2) => getScatterLineElements(graphDiv, click, pt2)).filter((candidate) => !!candidate);
  const best = minBy(lineCandidates, "d");
  const pointsInRadius = uniqBy(
    lineCandidates.filter(({ d }) => d < CANDIDATE_PICK_RADIUS).sort((a, b) => a.d - b.d).map(({ pt: pt2 }) => pt2),
    "pointIndex"
  );
  if (!best) return null;
  const { el, pt, valuePx, valuePy } = best;
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const rel = {
    x: clamp01((valuePx - rect.left) / Math.max(rect.width, 1)),
    y: clamp01((valuePy - rect.top) / Math.max(rect.height, 1))
  };
  return { rel, el, pt, pointsInRadius };
};
const scatterOnClickPopover = {
  buildAnchor: makeScatterAnchor,
  renderPopover: dropdownPopover(CartesianOnClickPopoverDropdown)
};
export default scatterOnClickPopover;
